// The frame every field type shares: label, hint, error and the aria wiring (spec §8: every field has a label, aria-invalid
// and aria-describedby). A field component renders its control inside it.
import { h } from "../h.js";
import { slot_props } from "../styles.js";
import { GRID_KEYS, grid_of } from "../constants.js";

export const dom_id = (form_id, path) => `${form_id || "form"}-${String(path).replace(/[^A-Za-z0-9_-]/g, "-")}`;

// the attributes the control itself needs: id, name, invalid state, the hint/error it is described by, required, disabled
export const control_aria = ({ id, field, error, required, disabled }) => {
    const described = [field?.hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ");
    return {
        id,
        name: id,
        disabled: disabled ? true : undefined,
        "aria-invalid": error ? "true" : undefined,
        "aria-required": required ? "true" : undefined,
        "aria-describedby": described === "" ? undefined : described,
    };
};

/*
 * props: field, id, error (text), required, disabled, styles, class_names, type (for styles.<type>), as_fieldset
 * (a group of controls: the label becomes the legend), variant (a data attribute for the CSS), children.
 */
export const FieldShell = ({ field, id, error, required, disabled, styles, class_names, type, as_fieldset = false, variant, children }) => {
    const style_args = { class_names, styles, type: type || field?.type };
    const root_extra = [class_names?.[GRID_KEYS[grid_of(field)]], error ? class_names?.is_invalid : null, disabled ? class_names?.is_disabled : null].filter(Boolean).join(" ");
    const label_children = [field?.label, required ? h("span", { key: "mark", "aria-hidden": "true", className: class_names?.required_mark }, " *") : null];
    return h(
        as_fieldset ? "fieldset" : "div",
        {
            ...slot_props({ slot: "root", extra: root_extra, ...style_args }),
            "data-field-type": field?.type,
            "data-field-key": field?.key,
            "data-direction": field?.direction || undefined,
            "data-invalid": error ? "true" : undefined,
            "data-variant": variant,
            ...(as_fieldset ? { "aria-describedby": [field?.hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined } : {}),
        },
        as_fieldset
            ? h("legend", slot_props({ slot: "label", ...style_args }), ...label_children)
            : h("label", { ...slot_props({ slot: "label", ...style_args }), htmlFor: id }, ...label_children),
        children,
        field?.hint ? h("p", { id: `${id}-hint`, ...slot_props({ slot: "hint", ...style_args }) }, field.hint) : null,
        error ? h("p", { id: `${id}-error`, role: "alert", ...slot_props({ slot: "error", ...style_args }) }, error) : null,
    );
};
